import { type } from "arktype";

import { djs, SINDI_TIME_ZONE } from "@/domain/time";
import type { QiveBankBillet } from "@/qive/schemas";

export type InboxSource = "QIVE" | "MANUAL" | "LOGISTICA";

export const INBOX_SOURCE_LABELS: Record<InboxSource, string> = {
	QIVE: "Qive",
	MANUAL: "Manual",
	LOGISTICA: "Módulo Logístico",
};

export const INBOX_SOURCE_DESCRIPTIONS: Record<InboxSource, string> = {
	QIVE: "Boleto capturado pela Qive",
	MANUAL: "Equipe",
	LOGISTICA: "Ordem de compra confirmada no Módulo Logístico",
};

export const manualInboxCandidateSchema = type({
	"supplierId?": "string >= 1",
	"creditorName?": "string >= 1",
	amount: "number > 0",
	dueOn: "string >= 1",
	description: "string >= 1",
});

export interface InboxCandidateInput {
	id: string;
	source: InboxSource;
	externalId: string | null;
	supplierId: string | null;
	creditorName: string;
	creditorTaxId: string | null;
	amountCents: number;
	currencyCode: "BRL";
	issuedOn: string | null;
	dueOn: string;
	description: string;
	sourceStatus: string | null;
	sourceTrustLevel: string | null;
	createdBy: string | null;
}

export interface InboxQiveDocumentReference {
	type: "NFE" | "NFSE";
	externalId: string;
	documentIdentifier: string;
	sourceConfidence: string | null;
}

export interface InboxFiscalDocumentLinkInput {
	candidateId: string;
	documentId: string;
	sourceConfidence: string | null;
	matchBasis:
		| "QIVE_LINKED"
		| "EXACT_ISSUER_AMOUNT_ISSUE_DATE"
		| "BALANCED_INSTALLMENT_GROUP"
		| "ISSUER_AMOUNT_NEAR_ISSUE_DATE";
}

export function inboxHorizon(now: Date | string = new Date()) {
	const today = djs(now).tz(SINDI_TIME_ZONE);
	const start = today.startOf("month");

	return {
		today: today.format("YYYY-MM-DD"),
		from: start.format("YYYY-MM-DD"),
		to: start.add(1, "month").endOf("month").format("YYYY-MM-DD"),
	};
}

function qiveAmountInCents(value: string) {
	const normalized = value.trim();

	if (!/^\d{1,3}(?:\.\d{3})*,\d{2}$/u.test(normalized)) {
		throw new Error(`A Qive devolveu um valor de boleto inválido: ${value}`);
	}

	const cents = Number(normalized.replace(/[.,]/gu, ""));

	if (!Number.isSafeInteger(cents) || cents <= 0) {
		throw new Error(`A Qive devolveu um valor de boleto inválido: ${value}`);
	}

	return cents;
}

function qiveCandidateId(externalId: string) {
	const hash = new Bun.CryptoHasher("sha256").update(externalId).digest("hex");

	return `qive:billet:${hash}`;
}

function qiveCreditor(billet: QiveBankBillet) {
	const finalName = billet.final_beneficiary?.name?.trim();
	const finalTaxId = billet.final_beneficiary?.document?.replace(/\D/gu, "");

	if (
		finalName &&
		finalTaxId &&
		/^(?:\d{11}|\d{14})$/u.test(finalTaxId) &&
		!/^0+$/u.test(finalTaxId)
	) {
		return { name: finalName, taxId: finalTaxId };
	}

	return {
		name: billet.beneficiary.name.trim(),
		taxId: billet.beneficiary.document.replace(/\D/gu, ""),
	};
}

export const InboxCandidates = {
	fromQive(billet: QiveBankBillet): {
		candidate: InboxCandidateInput;
		documentLinks: InboxQiveDocumentReference[];
	} {
		if (
			!/^\d{4}-\d{2}-\d{2}$/u.test(billet.due_date) ||
			djs(billet.due_date).format("YYYY-MM-DD") !== billet.due_date
		) {
			throw new Error("A Qive devolveu um vencimento de boleto inválido");
		}
		if (!/^(?:\d{11}|\d{14})$/u.test(billet.owner.document.replace(/\D/gu, ""))) {
			throw new Error("A Qive devolveu o proprietário de boleto inválido");
		}
		if (!/^\d{44,48}$/u.test(billet.digitable_line.replace(/\D/gu, ""))) {
			throw new Error("A Qive devolveu uma linha digitável inválida");
		}
		if (
			!/^\d{4}-\d{2}-\d{2}$/u.test(billet.issue_date) ||
			djs(billet.issue_date).format("YYYY-MM-DD") !== billet.issue_date
		) {
			throw new Error("A Qive devolveu uma emissão de boleto inválida");
		}

		const externalId = `${billet.owner.document.replace(/\D/gu, "")}:${billet.digitable_line.replace(/\D/gu, "")}`;
		const candidateId = qiveCandidateId(externalId);
		const creditor = qiveCreditor(billet);

		if (!/^(?:\d{11}|\d{14})$/u.test(creditor.taxId) || /^0+$/u.test(creditor.taxId)) {
			throw new Error("A Qive devolveu o credor de boleto inválido");
		}

		return {
			candidate: {
				id: candidateId,
				source: "QIVE",
				externalId,
				supplierId: null,
				creditorName: creditor.name,
				creditorTaxId: creditor.taxId,
				amountCents: qiveAmountInCents(billet.cost),
				currencyCode: "BRL",
				issuedOn: billet.issue_date,
				dueOn: billet.due_date,
				description: billet.document_number?.trim()
					? `Boleto ${billet.document_number.trim()}`
					: `Boleto de ${creditor.name}`,
				sourceStatus: billet.status,
				sourceTrustLevel: billet.trust_level?.trim() || null,
				createdBy: null,
			},
			documentLinks: billet.linked_documents.map((document) => ({
				type: document.document_type.toUpperCase() === "NFE" ? "NFE" : "NFSE",
				externalId: document.search_id,
				documentIdentifier: document.document_identifier,
				sourceConfidence: billet.trust_level?.trim() || null,
			})),
		};
	},

	manual(input: {
		supplierId?: string;
		creditorName?: string;
		amount: number;
		dueOn: string;
		description: string;
		actor: string;
	}): InboxCandidateInput {
		const supplierId = input.supplierId?.trim();
		const creditorName = input.creditorName?.trim();
		const parsed = manualInboxCandidateSchema.assert({
			...(supplierId ? { supplierId } : {}),
			...(creditorName ? { creditorName } : {}),
			amount: input.amount,
			dueOn: input.dueOn,
			description: input.description.trim(),
		});

		if (!parsed.supplierId && !parsed.creditorName) {
			throw new Error("Selecione um fornecedor ou informe o credor.");
		}
		if (
			!/^\d{4}-\d{2}-\d{2}$/u.test(parsed.dueOn) ||
			djs(parsed.dueOn).format("YYYY-MM-DD") !== parsed.dueOn
		) {
			throw new Error("Informe uma data de vencimento válida.");
		}

		const horizon = inboxHorizon();

		if (parsed.dueOn < horizon.from || parsed.dueOn > horizon.to) {
			throw new Error("O vencimento deve estar no mês atual ou no próximo mês.");
		}

		const amountCents = Math.round(parsed.amount * 100);

		if (
			!Number.isSafeInteger(amountCents) ||
			amountCents <= 0 ||
			Math.abs(parsed.amount * 100 - amountCents) > 1e-8
		) {
			throw new Error("Informe um valor válido.");
		}

		return {
			id: `manual:${crypto.randomUUID()}`,
			source: "MANUAL",
			externalId: null,
			supplierId: parsed.supplierId ?? null,
			creditorName: parsed.creditorName ?? "",
			creditorTaxId: null,
			amountCents,
			currencyCode: "BRL",
			issuedOn: null,
			dueOn: parsed.dueOn,
			description: parsed.description,
			sourceStatus: null,
			sourceTrustLevel: null,
			createdBy: input.actor,
		};
	},
};
